import type { I18n, IntlNumberFormats, PluralizationRulesMap } from 'vue-i18n'
import { createI18n } from 'vue-i18n'

export const supportedLanguages = ['en', 'de']

const messages = Object.fromEntries(
  Object.entries(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    import.meta.glob<{ default: any }>('./locales/*.json', { eager: true }))
    .map(([key, value]) => [key.slice(10, -5), value.default]),
)

// Typed, so createI18n picks its Composition API (legacy: false) signature, where
// i18n.global.locale is a ref (composables/language.ts).
const numberFormats: IntlNumberFormats = {
  'en-US': {
    currency: {
      style: 'currency', currency: 'USD', notation: 'standard', minimumFractionDigits: 0, maximumFractionDigits: 2,
    },
    decimal: {
      style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2,
    },
    percent: {
      style: 'percent', useGrouping: false,
    },
  },
  'sk-SK': {
    currency: {
      style: 'currency',
      currency: 'EUR',
      useGrouping: true,
      currencyDisplay: 'symbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    },
    decimal: {
      style: 'decimal', minimumSignificantDigits: 2, maximumSignificantDigits: 2,
    },
    percent: {
      style: 'percent', useGrouping: false,
    },
  },
}

export function localeTitle(locale: string): string {
  switch (locale) {
    case 'en':
      return 'English'
    case 'sk':
      return 'Slovensky'
    case 'pl':
      return 'Polski'
    case 'cs':
      return 'Česky'
    case 'hu':
      return 'Magyar'
    case 'de':
      return 'Deutsch'
    case 'se':
      return 'Svenska'
    case 'it':
      return 'Italiano'
    case 'fr':
      return 'Français'
    default:
      return locale
  }
}

export function localeFlag(locale: string): string {
  switch (locale) {
    case 'en':
      return 'gb'
    case 'cs':
      return 'cz'
    default:
      return locale
  }
}

// Typed without a message schema: inferring one from `messages` makes `const { t } = i18n.global`
// too deep for tsc (TS2589). Keys are checked by plugins/i18n/locales.spec.ts instead.
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- vue-i18n's own defaults for "no schema"
const i18n: I18n<{}, {}, {}, string, false> = createI18n({
  legacy: false,

  locale: parseDefaultLocale(),
  fallbackLocale: supportedLanguages[0],
  messages,

  numberFormats,
  fallbackWarn: false,
  missingWarn: false,
  pluralRules: pluralizationRules(),
})

export default i18n

function pluralizationRules(): PluralizationRulesMap {
  return supportedLanguages.reduce((rules, locale) => ({
    ...rules, [locale]: pluralizationRule,
  }), {})
}

function pluralizationRule(choice: number): number {
  if (choice <= 1)
    return choice

  return choice <= 4 ? 2 : 3
}

function parseDefaultLocale(): string {
  const browserLanguage = window.navigator.language.split('-')[0]

  return isLanguageSupported(browserLanguage) ? browserLanguage : supportedLanguages[0]
}

export function isLanguageSupported(locale: string | undefined): boolean {
  return !!locale && supportedLanguages.includes(locale)
}
